// src/contexts/CurrencyContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { API_BASE } from "../services/apiClient";

const CurrencyContext = createContext();

export function CurrencyProvider({ children, initialCurrency }) {
  const [currency, setCurrency] = useState(() => initialCurrency || localStorage.getItem("moedaSelecionada") || "EUR");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [currencySymbol, setCurrencySymbol] = useState('€');
  const [currenciesData, setCurrenciesData] = useState([]);
  const CACHE_KEY = 'exchange_rates_cache_v1';
  const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 horas
  const ERROR_BACKOFF_MS = 30 * 60 * 1000; // 30 minutos
  // De-duplicação de requisições em voo por ISO
  const inFlightRef = React.useRef({});
  const initialMountRef = React.useRef(true);
  // Supressão para o duplo invoke do StrictMode em dev
  const suppressUntilTsRef = React.useRef(0);

  // Carrega lista de moedas e símbolos no início
  useEffect(() => {
    const base = String(API_BASE || '').replace(/\/+$/, '');
    const url = /\/api$/i.test(base) ? `${base.replace(/\/api$/i, '')}/api/moedas` : `${base}/moedas`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (data && data.Registros) {
          setCurrenciesData(data.Registros);
        }
      })
      .catch(() => {
        // Mantém o estado atual e evita Uncaught (in promise) em caso de API indisponível
      });
  }, []);

  useEffect(() => {
    if (!initialCurrency) return;
    setCurrency(initialCurrency);
  }, [initialCurrency]);

  // Helper para normalizar o valor de moeda para um ISO de 3 letras sempre que possível
  const normalizeCurrency = (val) => {
    if (!val) return 'EUR';
    const map = {
      'R$': 'BRL',
      'US$': 'USD',
      '€': 'EUR',
      '£': 'GBP'
    };
    // Se vier um símbolo, converte
    if (map[val]) return map[val];
    // Se vier algo como 'BRL' já está ok
    const upper = String(val).trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(upper)) return upper;
    // Tenta encontrar por nome/iso no catálogo carregado
    const found = currenciesData.find(m => [m.isoMOEDA, m.nomeMOEDA, m.simboloMOEDA].map(x => String(x).toUpperCase()).includes(upper));
    if (found?.isoMOEDA) return found.isoMOEDA;
    return 'EUR';
  };

  // Atualiza localStorage, busca câmbio e símbolo ao trocar moeda
  useEffect(() => {
    const iso = normalizeCurrency(currency);
    localStorage.setItem("moedaSelecionada", iso);

    // Busca símbolo na lista carregada
    const found = currenciesData.find(m => m.isoMOEDA === iso);
    setCurrencySymbol(found ? found.simboloMOEDA : '');

    // Busca câmbio com SWR + de-dup + backoff
    if (iso !== "EUR" && /^[A-Z]{3}$/.test(iso)) {
      let cache = {};
      try { cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); } catch(_) { cache = {}; }
      const now = Date.now();
      const cached = cache[iso];

      // Usa imediatamente o cache disponível (mesmo que esteja stale)
      if (cached && typeof cached.rate === 'number') {
        setExchangeRate(cached.rate);
      }

      // Se é o primeiro mount e não há cache, evita disparar a primeira requisição (usa rate=1)
      if (!cached && initialMountRef.current) {
        initialMountRef.current = false;
        // Em dev, StrictMode invoca efeitos 2x; define janela de supressão
        suppressUntilTsRef.current = Date.now() + 3000; // 3s
        return;
      }

      // Se ainda dentro da janela de supressão e sem cache, não buscar
      if (!cached && Date.now() < suppressUntilTsRef.current) {
        return;
      }

      // Se cache fresco, não refaz request
      if (cached && (now - (cached.ts || 0) < CACHE_TTL_MS)) {
        return;
      }

      // Se erro recente, respeita backoff e não tenta agora
      if (cached && cached.errorTs && (now - cached.errorTs < ERROR_BACKOFF_MS)) {
        return;
      }

      // De-dup: se já existe uma requisição em voo para este ISO, reutiliza
      const existing = inFlightRef.current[iso];
      if (existing) {
        existing
          .then((rate) => {
            if (typeof rate === 'number') setExchangeRate(rate);
          })
          .catch(() => {})
          .finally(() => {});
        return;
      }

      const promise = fetch(`https://api.apilayer.com/fixer/latest?base=EUR&symbols=${iso}`, {
        headers: { apikey: import.meta.env.VITE_API_LAYER_KEY }
      })
        .then(async res => {
          if (!res.ok) throw new Error(`fixer ${res.status}`);
          const data = await res.json();
          if (data && data.success && data.rates && typeof data.rates[iso] === 'number') {
            const rate = data.rates[iso];
            setExchangeRate(rate);
            const newCache = { ...cache, [iso]: { rate, ts: Date.now() } };
            try { localStorage.setItem(CACHE_KEY, JSON.stringify(newCache)); } catch(_) {}
            return rate;
          }
          throw new Error('invalid data');
        })
        .catch(() => {
          // Marca erro para backoff e mantém valor em cache se existir
          const newCache = { ...cache, [iso]: { ...(cache[iso] || {}), errorTs: Date.now() } };
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(newCache)); } catch(_) {}
          if (cached && typeof cached.rate === 'number') {
            setExchangeRate(cached.rate);
            return cached.rate;
          } else {
            setExchangeRate(1);
            return 1;
          }
        })
        .finally(() => {
          delete inFlightRef.current[iso];
        });

      inFlightRef.current[iso] = promise;
      return;
    } else {
      setExchangeRate(1);
    }
    // Marca fim do primeiro ciclo após processamento
    initialMountRef.current = false;
  }, [currency, currenciesData]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, currencySymbol, exchangeRate, currenciesData }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}